import type { Tag } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import type {
  Realty,
  RealtyCity,
  RealtyOfferType,
  RealtyType,
  RealtyVillage,
} from '../../types/realty';

import type {
  GenericRequest,
  PaginationMeta,
  PaginationQuery,
} from '@eliadmin/core/src/api/api-types';

import { buildAuthHeader, fetchData } from '@eliadmin/core/src/api/fetcher';
import {
  buildStoreListRequest,
  buildWhereParams,
  storeApiResources,
  type ApiSort,
} from '@eliadmin/core/src/api/store-list-request';
import {
  buildRealtyWhere,
  type RealtyNumericInput,
  type RealtyWhere,
} from '@eliadmin/core/src/api/realty/realty-list-request';
import {
  normalizeRealtyFilter,
  parseRealtyFilterNumber,
  serializeRealtyFilter,
} from '../../utils/realty-filter-contract';
import type { RealtyOrder, RealtyOrderBy } from '../../utils/realty-filter-contract';

export type FetchProductQuery = PaginationQuery & {
  favorite?: boolean;
  search?: string;
  filter?: string;
  order?: 'asc' | 'desc';
  orderBy?: 'id' | 'price' | 'sells' | 'position' | 'views';
  price_from?: number;
  price_to?: number;
  categories?: boolean;
  /**
   * filter_geoRadius = 50.000000,19.000000,100 -> lat,lng,radius_in_meters
   */
  filter_geoRadius?: string;
  images?: boolean;
};

export type FetchRealtyQuery = {
  where?: RealtyWhere;
  sort?: ApiSort<typeof storeApiResources.storefrontRealtyRealties>;
  order?: 'asc' | 'desc';
  orderBy?: 'id' | 'price';
  'order[]'?: ('asc' | 'desc')[];
  'orderBy[]'?: ('id' | 'price')[];
  price_from?: RealtyNumericInput;
  price_to?: RealtyNumericInput;
  area_from?: RealtyNumericInput;
  area_to?: RealtyNumericInput;
  search?: string;
  page?: number;
  results?: number;
  images?: boolean;
};

export async function fetchRealties({
  locale,
  authToken,
  query,
  signal,
}: GenericRequest<{
  authToken?: string;
  query?: FetchRealtyQuery;
}> & { signal?: AbortSignal }) {
  const { where, sort, price_from, price_to, area_from, area_to, ...queryParams } =
    query ?? {};
  const rangeWhere = buildRealtyWhere({
    priceFrom: price_from,
    priceTo: price_to,
    areaFrom: area_from,
    areaTo: area_to,
  });
  const hasRanges = Object.keys(rangeWhere).length > 0;
  const selectedWhere = where && Object.keys(where).length > 0 ? where : undefined;

  const parsed = await fetchData<{ data: Realty[]; meta: PaginationMeta }>({
    ...buildStoreListRequest(storeApiResources.storefrontRealtyRealties, {
      query: queryParams,
      where: hasRanges
        ? selectedWhere
          ? { and: [selectedWhere, rangeWhere] }
          : rangeWhere
        : selectedWhere,
      sort,
    }),
    locale,
    headers: buildAuthHeader(authToken),
    signal,
  });
  return parsed;
}

export function buildRealtyFilterWhere({
  type_id,
  offer_type_id,
  city_id,
  village_id,
  area_from,
  area_to,
  number_of_rooms,
  under_construction,
}: {
  type_id?: (string | number)[];
  offer_type_id?: (string | number)[];
  city_id?: string | number;
  village_id?: string | number;
  area_from?: RealtyNumericInput;
  area_to?: RealtyNumericInput;
  number_of_rooms?: number[];
  under_construction?: boolean;
}): RealtyWhere {
  const conditions: RealtyWhere = {};

  const normalizeIds = (values: readonly (string | number)[] | undefined) =>
    [
      ...new Set((values ?? []).map(Number).filter(value => Number.isInteger(value) && value > 0)),
    ].sort((left, right) => left - right);

  const normalizedTypeIds = normalizeIds(type_id);
  const normalizedOfferTypeIds = normalizeIds(offer_type_id);
  const normalizedCityId = Number(city_id);
  const normalizedVillageId = Number(village_id);
  const normalizedRooms = [
    ...new Set(
      (number_of_rooms ?? []).filter(value => Number.isInteger(value) && value >= 0 && value <= 5),
    ),
  ].sort((left, right) => left - right);

  if (normalizedTypeIds.length) conditions.typeId = { in: normalizedTypeIds };
  if (normalizedOfferTypeIds.length) conditions.offerTypeId = { in: normalizedOfferTypeIds };

  if (Number.isInteger(normalizedCityId) && normalizedCityId > 0)
    conditions.cityId = { equals: normalizedCityId };
  if (Number.isInteger(normalizedVillageId) && normalizedVillageId > 0)
    conditions.villageId = { equals: normalizedVillageId };
  const normalizedArea = normalizeRealtyFilter({
    area_from: parseRealtyFilterNumber(area_from),
    area_to: parseRealtyFilterNumber(area_to),
  });
  const areaWhere = buildRealtyWhere({
    areaFrom: normalizedArea.area_from,
    areaTo: normalizedArea.area_to,
  });
  Object.assign(conditions, areaWhere);

  const exactRooms = normalizedRooms.filter(value => value < 5);
  const includesFivePlus = normalizedRooms.includes(5);

  if (exactRooms.length > 0 && includesFivePlus) {
    conditions.or = [
      { roomsCount: { in: exactRooms } },
      { roomsCount: { greater_than_equal: 5 } },
    ];
  } else if (includesFivePlus) {
    conditions.roomsCount = { greater_than_equal: 5 };
  } else if (exactRooms.length) {
    conditions.roomsCount = { in: exactRooms };
  }

  if (under_construction === true) conditions.underConstruction = { equals: true };

  return conditions;
}

/** Public query-fragment helper; active callers compose typed where before serializing once. */
export function buildRealtyFilterQuery(input: Parameters<typeof buildRealtyFilterWhere>[0]) {
  const where = buildRealtyFilterWhere(input);
  if (!Object.keys(where).length) return '';
  const serialized = buildWhereParams(where, storeApiResources.storefrontRealtyRealties);
  return new URLSearchParams(
    Object.entries(serialized).map(([key, value]) => [key, String(value)]),
  ).toString();
}

export const buildRealtySearchParams = (
  paramsObject: Record<
    string,
    string | number | boolean | Array<string | number | boolean> | null | undefined
  >,
  defaultOrder: string = 'desc',
  defaultOrderBy: string = 'id',
): string => {
  const firstValue = (value: (typeof paramsObject)[string]) =>
    Array.isArray(value) ? value[0] : value;
  const arrayValue = (value: (typeof paramsObject)[string]) =>
    (Array.isArray(value) ? value : value === undefined || value === null ? [] : [value]).map(
      item => Number(item),
    );

  return serializeRealtyFilter({
    type: firstValue(paramsObject.type)?.toString(),
    offer_type: arrayValue(paramsObject.offer_type),
    price_from: parseRealtyFilterNumber(firstValue(paramsObject.price_from)),
    price_to: parseRealtyFilterNumber(firstValue(paramsObject.price_to)),
    area_from: parseRealtyFilterNumber(firstValue(paramsObject.area_from)),
    area_to: parseRealtyFilterNumber(firstValue(paramsObject.area_to)),
    city: firstValue(
      paramsObject.city ?? paramsObject.location ?? paramsObject.location_id,
    )?.toString(),
    village: firstValue(paramsObject.village)?.toString(),
    number_of_rooms: arrayValue(paramsObject.number_of_rooms),
    is_new: firstValue(paramsObject.is_new) === true || firstValue(paramsObject.is_new) === 'true',
    search: firstValue(paramsObject.search)?.toString(),
    orderBy: (firstValue(paramsObject.orderBy)?.toString() ?? defaultOrderBy) as RealtyOrderBy,
    order: (firstValue(paramsObject.order)?.toString() ?? defaultOrder) as RealtyOrder,
  });
};

type FetchTagsSearchQuery = PaginationQuery & {
  search?: string;
};

export async function fetchTagsSearch({
  locale,
  authToken,
  query,
}: GenericRequest<{
  authToken?: string;
  query?: FetchTagsSearchQuery;
}>) {
  return fetchData<Tag[]>({
    ...buildStoreListRequest(storeApiResources.storefrontRealtyTags, { query }),
    locale,
    headers: buildAuthHeader(authToken),
  });
}

// finish all needed fetching functions of realties

export function fetchRealtyCities({ locale }: GenericRequest) {
  return fetchData<{ data: RealtyCity[] }>({
    ...buildStoreListRequest(storeApiResources.storefrontRealtyCities),
    locale,
  });
}

export function fetchRealtyVillages({ locale }: GenericRequest) {
  return fetchData<{ data: RealtyVillage[] }>({
    ...buildStoreListRequest(storeApiResources.storefrontRealtyVillages),
    locale,
  });
}

export function fetchRealtyOfferTypes({ locale }: GenericRequest) {
  return fetchData<{ data: RealtyOfferType[] }>({
    ...buildStoreListRequest(storeApiResources.storefrontRealtyOfferTypes),
    locale,
  });
}

export function fetchRealtyTypes({ locale }: GenericRequest) {
  return fetchData<{ data: RealtyType[] }>({
    ...buildStoreListRequest(storeApiResources.storefrontRealtyTypes),
    locale,
  });
}
